import { useMemo } from 'react';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';

export interface ExportableItems {
    images: Image3D[];
    structureSets: StructureSet[];
    doses: DoseMatrix[];
    registrations: RigidRegistration[];
    notExportedCount: number;
}

/**
 * Returns exportable items (new or previously exported)
 * and the count of items not yet exported.
 */
export default function useExportableItems(): ExportableItems {
    const patient = useSelectedPatient();
    // Every entity below (image, structure set, dose, registration) is
    // tracked, so one committed changeset — add, remove, or a setExported
    // — bumps the domain version and re-derives the list. This replaces
    // subscribing to four unrelated atoms as change proxies, which missed
    // any mutation that happened not to move one of them.
    const domainVersion = useDomainVersion();

    return useMemo(() => {
        const images =
            patient?.images.filter((img) => img.isNew || img.isExported) ?? [];
        const structureSets =
            patient?.structureSets.filter((ss) => ss.isNew || ss.isExported) ??
            [];
        const doses =
            patient?.doses.filter((dose) => dose.isNew || dose.isExported) ??
            [];
        const registrations =
            patient?.registrations.filter(
                (reg) => reg.isNew || reg.isExported
            ) ?? [];

        const allExportableItems = [
            ...images,
            ...structureSets,
            ...doses,
            ...registrations,
        ];
        const notExportedCount = allExportableItems.filter(
            (item) => item.isExported === false
        ).length;

        return {
            images,
            structureSets,
            doses,
            registrations,
            notExportedCount,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patient, domainVersion]);
}
